/**
 * auditorFormat.js — el payload del auditor convertido en texto.
 *
 * Aparte del JSX a propósito: es donde vive la única lógica del componente que
 * puede estar mal de verdad (los topes de Google y las métricas sin medir), y
 * aquí se puede probar sin montar un DOM.
 *
 * El backend manda claves y números, nunca frases: el texto lo pone la landing
 * en sus cinco idiomas (`landingAuditor.content.js`). Es el mismo
 * reparto que con las categorías de Places en `placeTypes.js`.
 */

/** El hex que le pasamos a `ScoreRing`, desde el color que manda el backend. */
export const RING_COLORS = {
  green: '#16a34a',
  blue: '#2563eb',
  amber: '#f59e0b',
  red: '#dc2626',
};

export function ringColor(color) {
  return RING_COLORS[color] || RING_COLORS.amber;
}

/**
 * Rellena los huecos `{loquesea}` de una plantilla.
 *
 * Acepta un número suelto (que va al hueco `{n}`, el caso de casi todas las
 * frases) o un objeto con las variables que manda el backend en `vars`. Un hueco
 * sin valor se deja tal cual en vez de imprimir «undefined»: si falta una
 * traducción se nota, pero no se pinta basura.
 */
export function fill(template, vars) {
  if (vars === null || vars === undefined) return String(template || '');
  const dict = typeof vars === 'object' ? vars : { n: vars };
  return String(template || '').replace(
    /\{(\w+)\}/g,
    (hueco, clave) => (clave in dict ? String(dict[clave]) : hueco),
  );
}

/** Los cuatro datos de la ficha, en el orden en que se leen. */
const PROFILE_KEYS = ['phone', 'website', 'hours', 'category'];

/**
 * El texto a la derecha de cada fila.
 *
 * Aquí están las dos honestidades que el informe no puede perder:
 *
 * - **`capped`**: Google no devuelve más de 10 fotos aunque haya cuatrocientas,
 *   así que cuando llega al tope se dice «10 o más», nunca «10».
 * - **`pts === null`**: la métrica no se ha medido (Google no publica la nota,
 *   o no ha devuelto ninguna reseña). Se dice que no hay dato. Un «0» diría
 *   «lo tienes mal» sobre algo que nadie ha mirado.
 */
export function blockValueText(block, c) {
  const r = c?.report || {};
  if (!block) return '';

  if (block.pts === null || block.pts === undefined) return r.unknown || '';

  switch (block.key) {
    case 'reviews_volume':
      return fill(r.reviewsCount, block.value ?? 0);

    case 'reviews_rating':
      return block.value === null || block.value === undefined
        ? r.noRating || ''
        : String(block.value);

    case 'reviews_pace':
      if (block.value === null || block.value === undefined) return r.unknown || '';
      return block.value <= 0 ? r.today || '' : fill(r.daysAgo, block.value);

    case 'profile': {
      const value = block.value || {};
      const missing = PROFILE_KEYS.filter((key) => !value[key]);
      if (missing.length === 0) return r.profileComplete || '';
      const names = missing.map((key) => (r.profileItems || {})[key] || key);
      return fill(r.profileMissing, { n: names.join(', ') });
    }

    case 'photos':
      if (block.capped) return r.photosCapped || '';
      if (!block.value) return r.noPhotos || '';
      return fill(r.photosCount, block.value);

    default:
      return '';
  }
}

/** El color del anillo de una fila, por lo bien o mal que va. */
export function blockRingColor(block) {
  if (!block || block.pts === null || block.pts === undefined) return '#cbd5e1';
  if (block.status === 'ok') return RING_COLORS.green;
  if (block.status === 'warn') return RING_COLORS.amber;
  return RING_COLORS.red;
}

/**
 * Una de las frases sueltas del informe («3 de tus 5 últimas reseñas están en
 * inglés»), con sus variables ya puestas.
 *
 * El idioma llega como código ISO y se traduce aquí: el backend no puede saber
 * en qué idioma está leyendo la landing quien lo mira.
 */
export function highlightText(highlight, c) {
  const r = c?.report || {};
  const plantilla = (r.highlights || {})[highlight?.key];
  if (!plantilla) return '';

  const vars = { ...(highlight.vars || {}) };
  if (vars.language) {
    vars.language = (r.languages || {})[vars.language] || vars.language;
  }
  return fill(plantilla, vars);
}

/**
 * El texto de una palanca: «Llegar a 75 reseñas».
 *
 * Es la tabla de puntuación leída al revés, no una estimación: si el siguiente
 * tramo de volumen está en 75, llegar ahí suma exactamente lo que suma. Por eso
 * se puede enseñar el número resultante sin prometer nada.
 */
export function leverText(lever, c) {
  const plantilla = ((c?.report || {}).levers || {})[lever?.key];
  if (!plantilla) return '';
  return fill(plantilla, { target: lever.target });
}

/** La línea de benchmark, con su porcentaje. Vacía si no hay plantilla. */
export function benchmarkText(benchmark, c) {
  const plantilla = ((c?.report || {}).benchmarks || {})[benchmark?.key];
  if (!plantilla) return '';
  return fill(plantilla, benchmark.delta_pct);
}
